import { formatGeoJSON } from 'screens/Property/helpers/formatGeoJson';
import { fieldsReducer } from 'screens/Property/helpers/propertyReducerHelpers';

/**
 * Creates a field feature with standardized properties
 * @param {Object} field - The field object containing geometry and properties
 * @param {string} farmId - The ID of the parent farm
 * @returns {Object} GeoJSON feature object
 */
export const createFieldFeature = (field, farmId) => ({
  type: 'Feature',
  geometry: field.geometry,
  properties: {
    ...field,
    id: field.id,
    name: field.name,
    $landType: 'field',
    $layer: 'default',
    propertyId: farmId,
    cropzones: field.cropzones || []
  }
});

/**
 * The shapes of one farm's fields and their crop zones, built exactly as the
 * landing page builds them (fieldsReducer), so a reloaded create/edit screen
 * shows the same background as one reached from the landing page. Only
 * features with a geometry are drawn.
 * @param {Object} farm - Farm object containing fields array
 * @returns {Array} GeoJSON features
 */
const farmFeatures = farm =>
  (farm?.fields || [])
    .reduce(fieldsReducer(farm.id), [])
    .filter(feature => feature?.geometry);

/**
 * Processes farm fields and creates a formatted GeoJSON FeatureCollection
 * @param {Object} farm - Farm object containing fields array
 * @returns {Object|null} Formatted GeoJSON FeatureCollection or null if no valid fields
 */
export const processFarmFields = farm => {
  if (!farm?.fields?.length) return null;

  return formatGeoJSON({
    type: 'FeatureCollection',
    features: farmFeatures(farm)
  });
};

/**
 * Processes all fields from multiple farms and creates a formatted GeoJSON FeatureCollection
 * @param {Array} properties - Array of farm/property objects
 * @returns {Object|null} Formatted GeoJSON FeatureCollection or null if no valid fields
 */
export const processAllFarmFields = properties => {
  const features = properties.flatMap(farmFeatures);

  if (!features.length) return null;

  return formatGeoJSON({
    type: 'FeatureCollection',
    features
  });
};
